import React from 'react';

import useMartyContext from 'hooks/useMartyContext';
import { track } from 'apis/amethyst';
import { evProductCalloutImpression } from 'events/product';
import { BADGE_MULTIPLIER_VERBIAGE } from 'constants/rewardsInfo';
import Tooltip from 'components/common/Tooltip';
import Vegan from 'components/icons/Vegan';
import EcoFriendly from 'components/icons/EcoFriendly';
import Recycled from 'components/icons/Recycled';
import Organic from 'components/icons/Organic';
import GiveBack from 'components/icons/GiveBack';
import VipOnlyLogo from 'components/icons/vipDashboard/VipOnlyLogo';
import { TaxonomyAttribute } from 'types/cloudCatalog';

import css from 'styles/components/productdetail/productCallout.scss';

export const VEGAN_ATTR = 'Vegan';
export const RECYCLED_ATTR = 'Recycled Material';
export const ORGANIC_ATTR = 'Organic';
export const ECO_FRIENDLY_ATTR = 'Sustainably Certified';
export const GIVE_BACK_ATTR = 'Give Back Products';
export const BONUS_POINTS_ATTR = 'VIP Point Multiplier';

interface ListElementProps {
  Icon?: React.ComponentType;
  label: string;
  svgSrc?: string;
  tooltipText: string;
}

export const ProductListElement = ({ Icon, label, tooltipText, svgSrc }: ListElementProps) => (
  <li>
    <Tooltip content={tooltipText}>
      {Icon &&
        <Icon aria-hidden={true} />
      }
      {svgSrc &&
        <img
          width={45}
          height={42}
          src={svgSrc}
          aria-hidden={true}
          alt="" />
      }
    </Tooltip>
    <span>{label}</span>
  </li>
);

interface ProductCalloutProps {
  brandId: string;
  rewardsBrandPromos: any; // TODO ts fix this once brandpromos ZCS is typed
  attributes: TaxonomyAttribute[];
  forKidsProductCallout: any; // TODO ts, not sure what the satus of this is, but could capture it in ZCS types
}

export const ProductCallout = (props: ProductCalloutProps) => {
  const { marketplace: { hasRewardsTransparency } } = useMartyContext();
  const { attributes: baseAttributes, brandId, forKidsProductCallout, rewardsBrandPromos = {} } = props;
  const attributes: { value: string }[] = [...baseAttributes ];

  /*
    checking for `rewardsBrandPromos`, and added an extra taxonomy attribute based on
    whether we're running a promo.
  */

  if (hasRewardsTransparency && rewardsBrandPromos[brandId]) {
    attributes.push({ value: BONUS_POINTS_ATTR });
  }
  const labelMap = attributes.filter(attribute => iconMap.get(attribute.value));

  const iconsWithLabels = labelMap.map(attribute => {
    const details = iconMap.get(attribute.value);
    if (!details) {
      return null;
    }
    const { Icon, label, tooltipText } = details;

    track(() => ([
      evProductCalloutImpression, attribute.value
    ]));

    return <ProductListElement
      key={label}
      Icon={Icon}
      label={label}
      tooltipText={tooltipText}
    />;
  });

  const {
    show: fkShow,
    brandId: fkBrandId,
    label: fkLabel,
    tooltipText: fkToolTipText,
    imgUrl: fkImgUrl
  } = forKidsProductCallout || {};
  if (fkShow === 'true' && brandId === fkBrandId) {
    iconsWithLabels.push(<ProductListElement
      key={fkLabel}
      svgSrc={fkImgUrl}
      label={fkLabel}
      tooltipText={fkToolTipText}
    />);
  }
  return <ul aria-label="Product Feature Callout" className={css.productCallout}> { iconsWithLabels } </ul>;
};

export default ProductCallout;

interface AttributeDetails {
  Icon: React.ComponentType;
  label: string;
  tooltipText: string;
}

// the keys of this map should match `attributeValueToEnum` in `events/product.js`,
const iconMap = new Map<string, AttributeDetails>([
  [VEGAN_ATTR, {
    Icon : Vegan,
    label : VEGAN_ATTR,
    tooltipText : '100% cruelty-free products constructed with zero animal parts.'
  }],
  [RECYCLED_ATTR, {
    Icon : Recycled,
    label : RECYCLED_ATTR,
    tooltipText : 'Composed of all or parts of materials from recycled goods, such as water bottles.'
  }],
  [ECO_FRIENDLY_ATTR, {
    Icon : EcoFriendly,
    label : 'Sustainably Certified',
    tooltipText : 'Certified by an industry-certifying organization to comply with at least one industry standard for environmental or socioeconomic impact.'
  }],
  [ORGANIC_ATTR, {
    Icon : Organic,
    label : ORGANIC_ATTR,
    tooltipText : 'Composed of organic cotton and other materials with a lower environmental impact.'
  }],
  [GIVE_BACK_ATTR, {
    Icon : GiveBack,
    label : 'Give Back',
    tooltipText : 'Charitable programs where brands make donations in kind or assist with humanitarian projects or organizations.'
  }],
  [BONUS_POINTS_ATTR, {
    Icon : VipOnlyLogo,
    label: 'Bonus Points',
    tooltipText: BADGE_MULTIPLIER_VERBIAGE
  }]
]);
